import type { Writable } from "svelte/store";
import type { FilesComConfig } from "./fs";
import { FS } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { throwIf } from "./util";

// Files.com does not answer other origins. Calls go through /api/filescom,
// which talks to the site's API. The site and the API key are request headers.

export const prop_filescom_mime = "filescom_mime";
export const prop_filescom_md5 = "filescom_md5";

class FileSystemFilesCom extends FS {
  supportsDirRename = true;

  constructor(config: FilesComConfig) {
    super(config);
  }

  get filesComConfig(): FilesComConfig {
    return this.config as FilesComConfig;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let js = await filesComCall(this.filesComConfig, mkURL("/api/filescom/list", "dir", dirPath));
      if (!Array.isArray(js)) {
        throw new Error("files.com list failed");
      }
      return FsDir.fromWire(dirPath, js);
    });
  }

  // A link that works for a short while without the API key.
  async getPublicURL(path: string): Promise<string> {
    let js = await filesComCall(this.filesComConfig, mkURL("/api/filescom/link", "path", path));
    throwIf(typeof js.url != "string" || js.url == "", "files.com gave no link");
    return js.url;
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let url = mkURL("/api/filescom/download", "path", path);
    let xhr = await filesComSend("GET", url, this.filesComConfig, undefined, httpProgress, "blob");
    return xhr.response as Blob;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let url = mkURL("/api/filescom/upload", "path", path);
    await filesComSend("PUT", url, this.filesComConfig, file, httpProgress);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let url = mkURL("/api/filescom/rename", "path", path, "newPath", newPath);
    await filesComCall(this.filesComConfig, url, "POST");
  }

  async createDirectory(dirPath: string): Promise<void> {
    await filesComCall(this.filesComConfig, mkURL("/api/filescom/mkdir", "path", dirPath), "POST");
  }

  // A folder goes with everything in it.
  async deleteFile(path: string): Promise<void> {
    await filesComCall(this.filesComConfig, mkURL("/api/filescom/delete", "path", path), "POST");
  }
}

// What the user typed as the site, as the server wants it: a subdomain of
// files.com or a custom domain, "" being the default site.
export function filesComSite(site: string): string {
  site = site.trim().toLowerCase();
  site = site.replace(/^https?:\/\//, "");
  let i = site.indexOf("/");
  if (i >= 0) {
    site = site.substring(0, i);
  }
  if (site.endsWith(".files.com")) {
    site = site.substring(0, site.length - ".files.com".length);
  }
  return site;
}

export function filesComHeaders(config: FilesComConfig): Record<string, string> {
  return {
    "X-Filescom-Key": config.apiKey,
    "X-Filescom-Site": config.site,
  };
}

export async function testFilesComConnection(config: FilesComConfig): Promise<void> {
  await filesComCall(config, "/api/filescom/test");
}

async function filesComCall(config: FilesComConfig, url: string, method = "GET"): Promise<any> {
  let rsp = await fetch(url, {
    method,
    headers: filesComHeaders(config),
  });
  let text = await rsp.text();
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  if (!rsp.ok) {
    throw new Error(js.error || `files.com ${rsp.status}`);
  }
  return js;
}

function filesComSend(
  method: string,
  url: string,
  config: FilesComConfig,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
  responseType?: XMLHttpRequestResponseType,
): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    if (responseType) {
      xhr.responseType = responseType;
    }
    for (let [k, v] of Object.entries(filesComHeaders(config))) {
      xhr.setRequestHeader(k, v);
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
        return;
      }
      filesComErrText(xhr).then(
        (msg) => reject(new Error(msg)),
        () => reject(new Error(`files.com ${xhr.status}`)),
      );
    };
    xhr.onerror = () => {
      reject(new Error("files.com request failed"));
    };
    if (onProgress) {
      let started = Date.now();
      let report = (ev: ProgressEvent) => {
        let total = ev.lengthComputable ? ev.total : 0;
        onProgress(ev.loaded, total, Date.now() - started);
      };
      if (method == "PUT") {
        xhr.upload.onprogress = report;
      } else {
        xhr.onprogress = report;
      }
    }
    if (data) {
      xhr.send(data);
    } else {
      xhr.send();
    }
  });
}

async function filesComErrText(xhr: XMLHttpRequest): Promise<string> {
  let text = "";
  if (xhr.responseType == "blob" && xhr.response instanceof Blob) {
    text = await xhr.response.text();
  } else if (xhr.responseType == "" || xhr.responseType == "text") {
    text = xhr.responseText || "";
  }
  if (text) {
    try {
      let js = JSON.parse(text);
      if (js.error) {
        return String(js.error);
      }
    } catch {
      // keep the status
    }
  }
  return `files.com ${xhr.status}`;
}

export function makeFileSystemFilesCom(config: FilesComConfig): FileSystemFilesCom {
  return new FileSystemFilesCom(config);
}
